import { from, Observable, OperatorFunction, pipe, share, Subscriber, Subscription, tap } from "rxjs";


/**
 * deduplicatingSwitchMap handles scenarios where multiple sources are requesting the same data simultaneously,
 * particularly useful for NgRx effects and actions making HTTP requests. It provides three key behaviors:
 *
 * 1. Deduplication: If multiple requests with the same payload arrive while a request is in flight,
 *    only the first request will be made. Subsequent identical requests are ignored until the first completes.
 *
 * 2. Cancellation: If a request with a different payload arrives while another is in flight,
 *    the current request is cancelled and a new one begins with the updated payload.
 *
 * 3. Fresh Start: Once a request completes, the operator accepts new requests again, even with the same payload.
 *
 * Example Scenarios:
 *
 * Scenario 1 - Deduplication:
 *   Action sequence: A1(payload1), A2(payload1), A3(payload1)
 *   Result: Only one HTTP request is made for payload1
 *
 * Scenario 2 - Cancellation & Update:
 *   Action sequence: A1(payload1), A2(payload1), A3(payload2)
 *   Result: Initial request for payload1 is cancelled, new request starts for payload2
 *
 * Scenario 3 - Fresh Start:
 *   Action sequence: A1(payload1) -> (completes) -> A2(payload1)
 *   Result: Two separate requests are made since the first completed
 */
export function deduplicatingSwitchMap<T, R>(project: (value: T) => Observable<R>, context: string = null): OperatorFunction<T, R> {
    return (source: Observable<T>): Observable<R> => {

        return new Observable<R>((subscriber: Subscriber<R>) => {
            // Tracks the active inner subscription (if any)
            let innerSubscription: Subscription = null;
            // Flag to track if the source has completed
            let isComplete = false;
            // Stores the current value being processed
            let currentValue: T | null = null;

            // Subscribe to the source observable
            const sourceSubscription = source.subscribe({
                next: (value: T): void => {
                    // Only process if the value is different from the current one or if this is the first value
                    if (JSON.stringify(value) !== JSON.stringify(currentValue) || currentValue === null) {
                        // Update current value being processed
                        currentValue = value;
                        // Cancel any existing subscription
                        innerSubscription?.unsubscribe();
                        // Start a new subscription with the current value
                        innerSubscription = from(project(value))
                        .subscribe({
                                next: (innerValue: R): void => {
                                    currentValue = null;
                                    // Forward the result to the subscriber
                                    subscriber.next(innerValue);
                                },
                                error: (err) => {
                                    currentValue = null;
                                    // Forward error to the subscriber
                                    subscriber.error(err);
                                },
                                complete: () => {
                                    // Only complete the outer subscriber if the source is complete
                                    isComplete && subscriber.complete();
                                },
                            });
                    }
                    // If value is the same, it's silently ignored (deduplication). In the care where you don't want it ignored, 
                    // use the shared version.
                },
                error: (err) => subscriber.error(err),
                complete: () => {
                    // Mark the source as complete
                    isComplete = true;
                    // Only complete if there's no active inner subscription
                    !innerSubscription && subscriber.complete();
                },
            });

            return () => {
                sourceSubscription?.unsubscribe();
                innerSubscription?.unsubscribe();
            };
        });
    };
}

/**
 * A shared version of deduplicatingSwitchMap that allows multiple subscribers to share the same execution.
 * This is useful when you want to avoid duplicate network requests across different components.
 * 
 * Without share():
 *
 * If Component A subscribes to your observable, it triggers a request
 * If Component B also subscribes to the same observable, it triggers another separate request
 * You end up with duplicate requests even though you're using deduplicatingSwitchMap
 *
 * With share():
 *
 * Component A subscribes and triggers a request
 * When Component B subscribes to the same observable, instead of creating a new execution of the entire chain, it simply subscribes to the existing execution
 * Both components receive the same response from a single request
 *
 * @param project - The function that transforms each value into an Observable
 * @param context - Optional context identifier for debugging
 * @returns An RxJS pipe that applies deduplicatingSwitchMap followed by share()
 */
export function sharedDeduplicatingSwitchMap<T, R>(project: (value: T) => Observable<R>, context: string = null) {
    return pipe(deduplicatingSwitchMap(project, context), share());
}
